import { useState, useLayoutEffect } from 'react'

/**
 * useFlipDropdown — Hook para auto-flip de menús/dropdowns.
 *
 * Mide altura real del menú con useLayoutEffect (pre-paint) y devuelve
 * posición {top, left, placement} con:
 *   - Auto-flip vertical si no cabe abajo.
 *   - Shift horizontal si se desborda a la derecha.
 *   - Fit-to-viewport si no cabe ni arriba ni abajo.
 *
 * Spec: docs/superpowers/specs/2026-05-19-icon-tooltips-pills-flag-dropdown.md
 *
 * Args:
 *   anchorX: number  — X del trigger en viewport coords
 *   anchorY: number  — Y del trigger
 *   menuRef: RefObject — ref al div del menú (para medir altura)
 *   isOpen: boolean  — solo recalcula cuando true
 *   margin: number   — margen al borde del viewport (default 8px)
 *
 * Returns:
 *   { top, left, placement }
 *     placement: 'bottom' (default), 'top' (auto-flipped), 'fit' (no cabe ni arriba ni abajo)
 */
export function useFlipDropdown({ anchorX, anchorY, menuRef, isOpen, margin = 8 }) {
  const [position, setPosition] = useState({
    top: anchorY,
    left: anchorX,
    placement: 'bottom',
  })

  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) return
    const menu = menuRef.current
    const menuH = menu.offsetHeight
    const menuW = menu.offsetWidth
    const vh = window.innerHeight
    const vw = window.innerWidth

    const spaceBelow = vh - anchorY - margin
    const spaceAbove = anchorY - margin

    let top, placement
    if (menuH <= spaceBelow) {
      top = anchorY
      placement = 'bottom'
    } else if (menuH <= spaceAbove) {
      top = anchorY - menuH
      placement = 'top'
    } else {
      top = Math.max(margin, vh - menuH - margin)
      placement = 'fit'
    }

    let left = anchorX
    if (left + menuW > vw - margin) {
      left = vw - menuW - margin
    }
    if (left < margin) {
      left = margin
    }

    setPosition({ top, left, placement })
  }, [isOpen, anchorX, anchorY, menuRef, margin])

  return position
}
